// 📄 pages/Admin/components/NuevaPlantillaModal.tsx
// Modal de alta de una plantilla nueva del Catálogo de APIs Estándar —
// antes esto se armaba inline al final de la lista (ver ApiCatalogSection.tsx),
// obligando a hacer scroll y mezclándose visualmente con las plantillas
// ya guardadas. Nada de esto toca `templates` en ApiCatalogSection.tsx
// hasta que el guardado sea exitoso — cancelar (o cerrar sin guardar)
// nunca deja un draft a medias en la lista.
import { useRef, useState } from "react";
import { X, Save } from "lucide-react";
import ConfirmDialog from "../../../components/ui/ConfirmDialog.tsx";
import ApiTemplateForm from "./ApiTemplateForm.tsx";
import { ApiTemplateData } from "../../../services/catalogoApisService.ts";
import { ApiTipoId } from "../../../services/clienteConexionService.ts";
import { TIPOS_API } from "../../../components/Configuracion/apiTiposConfig.ts";

interface NuevaPlantillaModalProps {
  tipo: ApiTipoId;
  onGuardar: (data: ApiTemplateData) => Promise<void>;
  onCancelar: () => void;
}

const plantillaVacia = (tipo: ApiTipoId): ApiTemplateData => ({
  tipo,
  authTipo: TIPOS_API[tipo]?.authTipo || "oauth",
  nombre: "",
  protocolo: "",
  dataCenter: "",
  urlBase: "",
  api: "",
  tokenUrl: "",
  grantType: "",
  endpoints: []
});

export default function NuevaPlantillaModal({ tipo, onGuardar, onCancelar }: NuevaPlantillaModalProps) {
  // Snapshot del estado "vacío" para detectar cambios sin guardar (ver
  // esDirty) — se congela una sola vez al abrir el modal.
  const plantillaInicial = useRef(plantillaVacia(tipo)).current;
  const [template, setTemplate] = useState<ApiTemplateData>(plantillaInicial);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [mostrarConfirmDescartar, setMostrarConfirmDescartar] = useState(false);

  const tipoMeta = TIPOS_API[tipo] || TIPOS_API.ariba;
  const esDirty = JSON.stringify(template) !== JSON.stringify(plantillaInicial);

  // Backdrop, botón X y "Cancelar" pasan por acá: si hay cambios sin
  // guardar, se confirma antes de perderlos — clic afuera nunca cierra
  // de golpe si el admin ya escribió algo.
  const intentarCerrar = () => {
    if (esDirty) {
      setMostrarConfirmDescartar(true);
      return;
    }
    onCancelar();
  };

  const handleGuardar = async () => {
    setGuardando(true);
    setError(null);
    try {
      await onGuardar(template);
    } catch (err: any) {
      setError(err.message || "Error al guardar la plantilla.");
    } finally {
      setGuardando(false);
    }
  };

  return (
    <>
      <div
        className="fixed inset-0 z-40 bg-black/70 flex items-center justify-center p-4"
        onClick={intentarCerrar}
      >
        {/* max-h-[90vh] + flex-col con header/footer flex-shrink-0 y el
            body flex-1 overflow-y-auto: header y footer siempre visibles,
            solo el formulario hace scroll — importante con varios
            endpoints cargados. */}
        <div
          className="bg-[rgb(var(--c-surface))] border border-white/10 rounded-2xl w-full max-w-2xl shadow-2xl max-h-[90vh] flex flex-col"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between p-5 border-b border-white/10 flex-shrink-0">
            <h3 className="text-lg font-medium text-white flex items-center gap-2">
              <span className="text-xl leading-none">{tipoMeta.icon}</span> Nueva plantilla · {tipoMeta.label}
            </h3>
            <button onClick={intentarCerrar} className="text-gray-400 hover:text-white">
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="p-5 overflow-y-auto flex-1">
            {error && (
              <div className="bg-red-500/10 border border-red-500/30 text-red-400 p-3 rounded-lg text-xs mb-4">{error}</div>
            )}
            <ApiTemplateForm template={template} onChange={setTemplate} />
          </div>

          <div className="flex items-center justify-end gap-3 p-5 border-t border-white/10 flex-shrink-0">
            <button
              type="button"
              onClick={intentarCerrar}
              className="px-4 py-2 rounded-lg text-sm font-medium text-gray-400 hover:text-white hover:bg-white/5 transition-colors"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={handleGuardar}
              disabled={guardando}
              className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 px-4 py-2 rounded-lg text-sm font-semibold text-white transition-colors"
            >
              <Save className="w-4 h-4" />
              {guardando ? "Guardando..." : "Guardar"}
            </button>
          </div>
        </div>
      </div>

      {mostrarConfirmDescartar && (
        <ConfirmDialog
          title="¿Descartar los cambios?"
          message="Todavía no guardaste esta plantilla — si descartas, se pierde lo que escribiste."
          confirmLabel="Sí, descartar"
          cancelLabel="Seguir editando"
          danger
          onConfirm={onCancelar}
          onCancel={() => setMostrarConfirmDescartar(false)}
        />
      )}
    </>
  );
}
